import { useMemo } from 'react';

import { Table } from '@tanstack/react-table';

import { TableFilterState } from '@/components/data-table/filters-v2';
import { useDataTable } from '@/hooks/use-data-table';
import { ApiError } from '@/modules/common/api/errors';

import { getTimeOffsTableColumns } from '../components/table/time-offs-table-columns';
import { TIME_OFFS_TABLE_ID } from '../constants/time-offs-filter-keys';
import { TimeOffRequest } from '../types/time-off-request';
import { useTimeOffRequests } from './use-time-off-requests';

interface UseTimeOffsTableResult {
  table: Table<TimeOffRequest>;
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  /** Resolved error message (i18n) or undefined. */
  errorMessage?: string;
  saveColumnSizing: () => void;
}

/**
 * Wires the time-off requests query to a TanStack table instance. Created
 * outside the table component so the page can also feed `table` to the
 * column-visibility dropdown (which must re-render when visibility toggles).
 * Column visibility + sizing persist to localStorage via `useDataTable`
 * (keyed on `TIME_OFFS_TABLE_ID`).
 */
export const useTimeOffsTable = (
  filters: TableFilterState,
  resolveErrorCode: (code: string) => string,
  {
    currentUserId,
    canViewOtherTimeOffs,
    shouldShowRequesterColumn,
    onEditRequest,
  }: {
    currentUserId: number | undefined;
    canViewOtherTimeOffs: boolean;
    shouldShowRequesterColumn: boolean;
    onEditRequest: (request: TimeOffRequest) => void;
  }
): UseTimeOffsTableResult => {
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } =
    useTimeOffRequests({ filters, currentUserId, canViewOtherTimeOffs });

  const rows = useMemo<TimeOffRequest[]>(() => data?.items ?? [], [data]);
  const columns = useMemo(
    () => getTimeOffsTableColumns({ shouldShowRequesterColumn, onEditRequest }),
    [shouldShowRequesterColumn, onEditRequest]
  );

  const { table, saveColumnSizing } = useDataTable<TimeOffRequest>({
    data: rows,
    columns,
    getRowId: row => String(row.id),
    tableId: TIME_OFFS_TABLE_ID,
  });

  const errorMessage = error
    ? error instanceof ApiError
      ? resolveErrorCode(error.code)
      : error.message
    : undefined;

  return {
    table,
    isLoading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    loadMore: fetchNextPage,
    errorMessage,
    saveColumnSizing,
  };
};
